import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import type { FormDefinition } from '@platform/contracts';
import { Alert, Button, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { FormBuilder } from '../../../features/forms/form-builder';
import { useFormsClient } from '../../../lib/api/forms-client-context';

export const Route = createFileRoute('/forms/$formId/')({
  component: BuilderPage,
});

/**
 * `/forms/:formId` — the builder route (MASTER_PLAN M8.5).
 *
 * Draft saves do not invalidate this query: the author's editing session owns the draft
 * while the page is open, and a refetch mid-edit would fight them for it. Publishing does
 * invalidate, because it creates the next draft and the session genuinely restarts.
 */
function BuilderPage() {
  const { formId } = Route.useParams();
  const client = useFormsClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const detail = useQuery({
    queryKey: ['intake-forms', formId],
    queryFn: () => client.get(formId),
  });

  const publishedVersion = detail.data?.form.publishedVersion ?? null;
  const published = useQuery({
    queryKey: ['intake-forms', formId, 'version', publishedVersion],
    enabled: publishedVersion !== null,
    queryFn: () => client.getVersion(formId, publishedVersion ?? 1),
  });

  const publish = useMutation({
    mutationFn: (notes: string) => client.publish(formId, { notes }),
    onSuccess: async (version) => {
      toast({ tone: 'success', message: `Version ${String(version.version)} is live.` });
      await queryClient.invalidateQueries({ queryKey: ['intake-forms'] });
    },
  });

  const saveDraft = async (schema: FormDefinition): Promise<void> => {
    await client.saveDraft(formId, schema);
    // Only the list is refreshed — `exact` keeps this page's own query out of it.
    await queryClient.invalidateQueries({ queryKey: ['intake-forms'], exact: true });
  };

  if (detail.isPending) {
    return (
      <>
        <PageHeader title="Loading form" />
        <Card className="p-6">
          <Spinner label="Loading the draft" />
        </Card>
      </>
    );
  }

  if (detail.isError) {
    return (
      <>
        <PageHeader title="Intake form" />
        <Alert tone="error">
          {detail.error instanceof Error ? detail.error.message : 'This form could not be loaded.'}
        </Alert>
      </>
    );
  }

  return (
    <FormBuilder
      /* A new draft is a new editing session: remount rather than reconcile. */
      key={`${formId}:${String(publishedVersion ?? 0)}`}
      detail={detail.data}
      publishedSchema={published.data?.schema ?? null}
      onSaveDraft={saveDraft}
      onPublish={(notes) => publish.mutateAsync(notes)}
      versionsLink={
        <Link to="/forms/$formId/versions" params={{ formId }}>
          <Button variant="secondary">Versions</Button>
        </Link>
      }
    />
  );
}
